import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, BehaviorSubject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { ProjectAccess, ProjectAccessAssignmentMask, ProjectAccessRoles, SignatureRole } from '@completion/models';
import { getCurrentProject, getProjectAccessList, getProjectSignatureMasks, getSignatureRoles } from '@completion/selectors';
import { ProjectAccessListLoad, ProjectSignatureMasksLoad, SignatureRoleLoad, UpdateProjectSignatureMask } from '@completion/actions';
import { SignatureRoleMatrixVm } from '../../../../shared/matrix/signature-role-matrix.service';

@Component({
  selector: 'app-signature-role-matrix',
  templateUrl: './signature-role-matrix.component.html',
  styleUrls: ['./signature-role-matrix.component.scss'],
  standalone: false,
})
export class SignatureRoleMatrixComponent implements OnInit, OnDestroy {
  // Base streams from store
  private projectAccessList$: Observable<ProjectAccess[]>;
  private signatureRoles$: Observable<SignatureRole[]>;
  private masks$: Observable<ProjectAccessAssignmentMask[]>;

  // Role filter stream (e.g. ADMIN, EDITOR, etc.)
  private readonly roleFilter$ = new BehaviorSubject<string>('');
  // Username filter stream
  private readonly userFilter$ = new BehaviorSubject<string>('');

  // Public view model the template binds to
  vm$: Observable<SignatureRoleMatrixVm>;

  private readonly destroy$ = new Subject<void>();

  roleFilter = '';
  userFilter = '';

  constructor(private readonly store: Store) {}

  ngOnInit(): void {
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((project) => {
        if (project) {
          this.store.dispatch(new ProjectAccessListLoad(project));
          this.store.dispatch(new ProjectSignatureMasksLoad(project));
          this.store.dispatch(new SignatureRoleLoad());
        }
      });

    this.projectAccessList$ = this.store.select(getProjectAccessList) as Observable<ProjectAccess[]>;
    this.signatureRoles$ = this.store.select(getSignatureRoles) as Observable<SignatureRole[]>;
    this.masks$ = this.store.select(getProjectSignatureMasks) as Observable<ProjectAccessAssignmentMask[]>;

    // Build a single view model stream so table columns, masks, and data stay in sync
    this.vm$ = combineLatest([this.projectAccessList$, this.signatureRoles$, this.masks$, this.roleFilter$, this.userFilter$]).pipe(
      map(([accessList, roles, masks, roleFilter, userFilter]) => {
        const safeRoles = roles || [];
        const list = accessList || [];
        const filteredByUser = this.filterByUser(list, userFilter || '');
        const filteredAccess = this.filterProjectAccessList(filteredByUser, roleFilter || '').sort((a, b) =>
          (a.user?.userName ?? '').localeCompare(b.user?.userName ?? ''),
        );
        const displayedColumns = ['username', ...safeRoles.map((role) => this.roleToColumn(role))];
        return {
          projectAccessList: filteredAccess,
          roles: safeRoles,
          masks: masks || [],
          displayedColumns,
        } as SignatureRoleMatrixVm;
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private filterProjectAccessList(accessList: ProjectAccess[], filter: string): ProjectAccess[] {
    const base = (accessList || []).filter((access) => access.role !== ProjectAccessRoles.READ_ONLY);
    if (!filter) {
      return base;
    }
    return base.filter((access) => access.role === filter);
  }

  private filterByUser(accessList: ProjectAccess[], filter: string): ProjectAccess[] {
    const list = accessList || [];
    const trimmed = (filter || '').trim();
    if (!trimmed) {
      return list;
    }
    const lower = trimmed.toLowerCase();
    return list.filter((pa) => pa.user && pa.user.userName && pa.user.userName.toLowerCase().includes(lower));
  }

  onRoleFilterChange(role: string): void {
    this.roleFilter = role;
    this.roleFilter$.next(role || '');
  }

  onUserFilterChange(value: string): void {
    this.userFilter = value || '';
    this.userFilter$.next(this.userFilter);
  }

  // --- Bitmask helpers (same pattern as AssignmentRoleMatrixComponent) ---

  private getMaskForProjectAccess(masks: ProjectAccessAssignmentMask[] | null | undefined, projectAccessId: number): number {
    if (!masks) {
      return 0;
    }
    const entry = masks.find((m) => m.projectAccessId === projectAccessId);
    return entry ? entry.mask : 0;
  }

  private isChecked(mask: number, ordinal: number): boolean {
    return (mask & (1 << ordinal)) !== 0;
  }

  private toggleBit(mask: number, ordinal: number): number {
    return mask ^ (1 << ordinal);
  }

  private isRoleInMask(mask: number, role: SignatureRole): boolean {
    if (role == null || (role as any).ordinal == null) {
      return false;
    }
    const ordinal = (role as any).ordinal as number;
    return this.isChecked(mask, ordinal);
  }

  hasSignatureRole(projectAccess: ProjectAccess, role: SignatureRole, masks: ProjectAccessAssignmentMask[] | null | undefined): boolean {
    const mask = this.getMaskForProjectAccess(masks, projectAccess.id);
    return this.isRoleInMask(mask, role);
  }

  toggleSignatureRole(projectAccess: ProjectAccess, role: SignatureRole, masks: ProjectAccessAssignmentMask[] | null | undefined): void {
    const currentMask = this.getMaskForProjectAccess(masks, projectAccess.id);
    if (role == null || (role as any).ordinal == null) {
      return;
    }
    const ordinal = (role as any).ordinal as number;
    const newMask = this.toggleBit(currentMask, ordinal);
    this.store.dispatch(new UpdateProjectSignatureMask(projectAccess.id, newMask));
  }

  getRoleIcon(projectAccess: ProjectAccess, role: SignatureRole, masks: ProjectAccessAssignmentMask[] | null | undefined): string {
    return this.hasSignatureRole(projectAccess, role, masks) ? 'check_circle' : 'radio_button_unchecked';
  }

  roleToColumn(role: SignatureRole): string {
    // Column ID used by matColumnDef and displayedColumns; must match shared matrix
    return `role-${role.id}`;
  }
}
